import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TicketMessage, TicketAttachment } from '../types/ticket';
import ImageThumbnail from './ImageThumbnail';

interface Props {
  message: TicketMessage;
  isMine: boolean;
}

export default function TicketMessageBubble({ message, isMine }: Props) {
  const { t } = useTranslation('tickets');
  let attachments: TicketAttachment[] = [];
  if (message.attachments) {
    try {
      attachments = JSON.parse(message.attachments) as TicketAttachment[];
    } catch {
      attachments = [];
    }
  }

  const align = isMine ? 'flex-end' : 'flex-start';
  const bg = isMine ? 'var(--primary-light)' : 'var(--surface-2)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: align, margin: '8px 0' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted, #888)', marginBottom: 2 }}>
        {message.authorName}
        <span style={{ marginLeft: 6 }}>
          {message.authorRole === 'admin' ? t('messageBubble.adminRole') : ''}
        </span>
        <span style={{ marginLeft: 6 }}>{new Date(message.createdAt).toLocaleString()}</span>
      </div>
      <div style={{ maxWidth: '70%', padding: '10px 12px', borderRadius: 10, background: bg }}>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message.content}</div>
        {attachments.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {attachments.map((a, idx) => (
              <AttachmentPreview key={`${a.url}-${idx}`} att={a} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentPreview({ att, t }: { att: TicketAttachment; t: (key: string, opts?: Record<string, unknown>) => string }) {
  if (att.type === 'image') {
    return <ImageThumbnail url={att.url} fileName={att.fileName} />;
  }
  if (att.type === 'video') {
    return (
      <a href={att.url} target="_blank" rel="noopener noreferrer">
        {att.fileName}{t('messageBubble.videoLink')}
      </a>
    );
  }
  return (
    <a href={att.url} target="_blank" rel="noopener noreferrer">
      {att.fileName}{t('messageBubble.fileLink')}
    </a>
  );
}
